<template>
  <div id="scadaManagerDialog">
    <main-large-dialog
      attach="#scadaManagerDialog"
      :is-visible.sync="computedIsVisible"
      :title="$i18n.t('dialog.title.scadaManager')"
      disagree-text="dialog.close"
      :has-agree="false"
      no-paddings-x
      no-paddings-y
      max-height="85vh"
      min-body-height="55vh"
      dialog-width="75%"
      header-shadow
    >
      <template #body>
        <v-col v-if="isFetched" cols="12" style="height: 100%; display: flex; flex-direction: column">
          <v-row justify="end" align="center" no-gutters>
            <v-col cols="3" class="pr-5 py-4">
              <data-input
                v-model="search"
                :data-type="{
                  name: 'text',
                }"
                prepend-inner-icon="mdi-magnify"
                :placeholder="$i18n.t('default.find')"
                hide-details
                clearable
              />
            </v-col>
          </v-row>
          <custom-table
            v-if="isTableMounted"
            :table-props="{ style: 'height: 100%' }"
            :headers="headers"
            :items="filtredMeasurements"
          >
            <template #[`customtable-header`]>
              <scada-manager-table-row
                is-new-measurement
                :scada-systems="scadaSystems"
                :measurements="measurements"
                :measurement-parameters="measurementParameters"
                @getMeasurements="getMeasurements"
              ></scada-manager-table-row>
            </template>
            <template #[`customtable-row`]="{ data }">
              <scada-manager-table-row
                :key="data.row.id"
                :measurement="data.row"
                :scada-systems="scadaSystems"
                :measurements="measurements"
                :measurement-parameters="measurementParameters"
                @getMeasurements="getMeasurements"
                @openDeletingDialog="openDeletingDialog"
                :currently-edited-measurement-id.sync="currentlyEditedMeasurementId"
              ></scada-manager-table-row>
            </template>
          </custom-table>
        </v-col>
        <main-loader class="pt-10" v-else />
      </template>
    </main-large-dialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.title.deleteScadaMeasurement')"
      :body="$i18n.t('dialog.body.deleteScadaMeasurementConfirmation')"
      agree-text="dialog.agreeDelete"
      :loading="isLoading"
      @agree="deleteMeasurementHandler"
    />
  </div>
</template>

<script>
import { get, call } from 'vuex-pathify';

import MainLargeDialog from '@/components/MainLargeDialog';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import ScadaManagerTableRow from '@/components/ScadaManagerTableRow';
import CustomTable from '@/components/CustomTable';

export default {
  name: 'ScadaManagerDialog',
  components: {
    MainLargeDialog,
    DataInput,
    MainLoader,
    CustomTable,
    ScadaManagerTableRow,
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    isVisible: {
      type: Boolean,
      required: false,
    },
  },
  data: () => ({
    search: '',
    isTableMounted: true,
    isFetched: false,
    isLoading: false,
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    currentlyEditedMeasurementId: null,
    scadaSystems: [],
    measurementParameters: [],
    measurements: [],
    measurementToDelete: null,
  }),
  computed: {
    scadaMeasurements: get('scada/scadaThreeMeasurements'),
    scadaDataSourceName: get('admin/modulesMapping@scada3.measurements_datasource_name'),
    headers() {
      const minWidth = `210px`;
      return [
        {
          value: 'external_name',
          text: this.$i18n.t('dialog.body.measurementSeriesIdentifier'),
          minWidth,
        },
        {
          value: 'verbose_name',
          text: this.$i18n.t('dialog.body.verboseName'),
          minWidth,
        },
        {
          value: 'system',
          text: this.$i18n.t('dialog.body.scadaType'),
          minWidth,
        },
        {
          value: 'measure_type',
          text: this.$i18n.t('dialog.body.measureType'),
          minWidth,
        },
        {
          value: 'report_period',
          text: this.$i18n.t('dialog.body.timeInterval'),
          minWidth,
        },
        {
          value: 'measurement_parameter',
          text: this.$i18n.t('dialog.body.parameter'),
          minWidth,
          optional: true,
        },
        {
          value: 'measurement_unit',
          text: this.$i18n.t('dialog.body.measurementUnit'),
          minWidth,
          optional: true,
        },
        {
          value: 'options',
          text: null,
        },
      ];
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    filtredMeasurements() {
      return this.search
        ? this.measurements.filter(
            measurement =>
              measurement.external_name.toLowerCase().includes(this.search.toLowerCase()) ||
              measurement.verbose_name.toLowerCase().includes(this.search.toLowerCase())
          )
        : this.measurements;
    },
  },
  mounted() {
    this.init();
  },
  watch: {
    computedIsVisible(nV) {
      if (!nV) return;
      /** Remounting table to reset it's state */
      this.search = '';
      this.currentlyEditedMeasurementId = null;
      this.isTableMounted = false;
      this.$nextTick(() => (this.isTableMounted = true));
      /** */
    },
  },
  methods: {
    deleteMeasurement: call('layers/deleteDatasourceFeature'),
    getScadaMeasurements: call('scada/getScadaThreeMeasurements'),
    getScadaSystems: call('scada/getScadaThreeSystems'),
    getScadaMeasurementParameters: call('scada/getScadaThreeMeasurementParameters'),
    getScadaSystemsVerboseName(system, type, name) {
      return name ? Object.values(this.scadaSystems[system][type]).find(el => el.name == name) : '';
    },
    getParametersVerboseName(name) {
      return name ? this.measurementParameters.find(parameter => parameter.name === name) : '';
    },
    async getMeasurements(withParse = true) {
      this.isFetched = false;
      if (withParse) await this.getScadaMeasurements(this.scadaDataSourceName);
      this.measurements = this.scadaMeasurements.map(feature => {
        return {
          ...feature,
          measure_type: this.getScadaSystemsVerboseName(feature.system, 'supported_measures', feature.measure_type),
          report_period: this.getScadaSystemsVerboseName(feature.system, 'aggregate_periods', feature.report_period),
          measurement_parameter: this.getParametersVerboseName(feature.measurement_parameter),
          isEditing: false,
          isSaving: false,
        };
      });
      this.isFetched = true;
    },
    openDeletingDialog(id) {
      this.measurementToDelete = id;
      this.isDeleteDialogVisible = true;
      this.isDeleteDialogLoaded = true;
    },
    async deleteMeasurementHandler() {
      const payload = {
        datasource: this.scadaDataSourceName,
        id: this.measurementToDelete,
      };
      try {
        this.isLoading = true;
        await this.deleteMeasurement(payload);
        await this.getMeasurements();
      } catch {
        //
      } finally {
        this.isLoading = false;
        this.isDeleteDialogVisible = false;
        this.isDeleteDialogLoaded = false;
        this.measurementToDelete = null;
      }
    },
    async init() {
      try {
        this.isFetched = false;
        const r = await Promise.all([this.getScadaMeasurementParameters(), this.getScadaSystems()]);
        this.measurementParameters = r[0];
        this.scadaSystems = r[1];
        await this.getMeasurements();
      } catch {
        //
      } finally {
        this.isFetched = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .maindialog-body-card-text {
  display: flex;
  overflow: hidden;
}
::v-deep .mainlargedialog-body-wrapper {
  width: 100%;
}

.search {
  position: sticky;
  right: 0;
  z-index: 1;
}
::v-deep .v-text-field__details {
  margin-bottom: 0 !important;
}
</style>
